import { ReactNode, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, ChevronRight, Close, Tune } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClientResponseDto } from '@bandido/shared/dtos';
import { OrderOptionsValues } from '@bandido/shared/enums';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { PaginationProps, ROWS_PER_PAGE_OPTIONS } from '@frontend/features/common/components/Table';
import { TablePagination } from '@frontend/features/common/components/Table/TablePagination';
import { SortingOptions } from '@frontend/features/common/types';
import { useRememberedScroll } from '@frontend/features/common/hooks/useRememberedScroll';
import { routes } from '@frontend/router/routes';

interface Props {
  items: ClientResponseDto[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isPreviousData: boolean;
  isError: boolean;
  onRetry: () => void;
  search: string;
  onSearch: (value: string) => void;
  onCreate: () => void;
  pagination: PaginationProps;
  sorting: SortingOptions;
  onSort: (options: SortingOptions) => void;
  filters: ReactNode;
  filtersCount?: number;
  onClearFilters: () => void;
  mineToggle: ReactNode;
}

// Fields accepted by the client repository's sort allowlist.
const sortOptions = [
  ['firstName', 'clients.view.firstName'],
  ['lastName', 'clients.view.lastName'],
  ['email', 'clients.view.email'],
  ['phoneNumber', 'clients.view.phoneNumber'],
  ['whatsappNumber', 'clients.view.whatsappNumber'],
  ['agencyName', 'clients.table.agency'],
  ['organizationName', 'clients.table.organization'],
  ['country', 'clients.view.country'],
  ['city', 'clients.view.city'],
  ['state', 'clients.mobile.state'],
  ['documentNumber', 'clients.view.documentNumber'],
  ['createdAt', 'clients.table.created'],
  ['updatedAt', 'common.updated'],
];

export const ClientsListMobile = ({
  items,
  totalCount,
  isLoading,
  isFetching,
  isPreviousData,
  isError,
  onRetry,
  search,
  onSearch,
  onCreate,
  pagination,
  sorting,
  onSort,
  filters,
  filtersCount,
  onClearFilters,
  mineToggle,
}: Props) => {
  const { t } = useTranslation();
  const { ability } = useAuth();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const scrollRef = useRememberedScroll(!isLoading && !isPreviousData && !isError);
  return (
    <Box
      ref={scrollRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        minWidth: 0,
        p: 2,
        overscrollBehavior: 'contain',
        '& button': { minHeight: 44, minWidth: 44 },
        '& input, & .MuiSelect-select': { fontSize: 16 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={2}>
        <Box minWidth={0}>
          <Typography component="h1" variant="h5">
            {t('clients.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('clients.subtitle')}
          </Typography>
        </Box>
        {ability?.can('manage', 'Clients') && (
          <Button variant="contained" startIcon={<Add />} onClick={onCreate}>
            {t('common.new')}
          </Button>
        )}
      </Stack>
      <Box mb={1} sx={{ '& .MuiTextField-root': { maxWidth: 'none' } }}>
        <SearchInput value={search} onSearch={onSearch} placeholder={t('common.search')} />
      </Box>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        {mineToggle}
        <Button
          variant="outlined"
          startIcon={
            <Badge badgeContent={filtersCount} color="error">
              <Tune />
            </Badge>
          }
          onClick={() => setFiltersOpen(true)}
        >
          {t('common.filters')}
        </Button>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <TextField
          select
          fullWidth
          size="small"
          label={t('common.mobileList.sort')}
          value={sorting.sort}
          onChange={(event) => onSort({ ...sorting, sort: event.target.value })}
        >
          {sortOptions.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {t(label)}
            </MenuItem>
          ))}
        </TextField>
        <IconButton
          aria-label={t(
            sorting.order === OrderOptionsValues.asc ? 'common.mobileList.ascending' : 'common.mobileList.descending',
          )}
          onClick={() =>
            onSort({
              ...sorting,
              order: sorting.order === OrderOptionsValues.asc ? OrderOptionsValues.desc : OrderOptionsValues.asc,
            })
          }
        >
          {sorting.order === OrderOptionsValues.asc ? <ArrowUpward /> : <ArrowDownward />}
        </IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {totalCount.toLocaleString()} {t('common.results')}
      </Typography>
      {isFetching && !isLoading && <LinearProgress aria-label={t('cases.list.refreshing')} />}
      {isError && (
        <Alert severity="error" action={<Button onClick={onRetry}>{t('common.refresh')}</Button>}>
          {t('clients.mobile.loadError')}
        </Alert>
      )}
      {isLoading ? (
        Array.from({ length: 4 }, (_, i) => <Skeleton key={i} variant="rounded" height={150} sx={{ mb: 1.5 }} />)
      ) : (
        <List disablePadding sx={{ display: 'grid', gap: 1.5 }}>
          {items.map((item) => (
            <Paper
              component="li"
              key={item.id}
              variant="outlined"
              sx={{ borderRadius: 2, minWidth: 0, overflowWrap: 'anywhere' }}
            >
              <ListItemButton
                component={RouterLink}
                to={routes.dashboard.clientView.replace(':id', item.id)}
                sx={{ display: 'block', p: 2, borderRadius: 2 }}
              >
                <Stack direction="row" gap={1} mb={1}>
                  <Typography fontWeight={600} flex={1} minWidth={0}>
                    {[item.firstName, item.lastName].filter(Boolean).join(' ') || t('common.unassigned')}
                  </Typography>
                  <ChevronRight color="action" />
                </Stack>
                <Typography variant="body2">{item.email || t('common.unassigned')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.phoneNumber
                    ? `+${item.phoneCountryCode || ''} ${item.phoneNumber}`
                    : item.whatsappNumber || t('common.unassigned')}
                </Typography>
                <Typography variant="caption" display="block" mt={1}>
                  {t('clients.table.preferredAgent')}:{' '}
                  {[item.preferredAgentFirstName, item.preferredAgentLastName].filter(Boolean).join(' ') ||
                    t('common.unassigned')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[item.agencyName, item.organizationName].filter(Boolean).join(' · ')}
                </Typography>
              </ListItemButton>
            </Paper>
          ))}
        </List>
      )}
      {!isLoading && !isError && !items.length && (
        <Typography py={4} textAlign="center">
          {t('common.noData')}
        </Typography>
      )}
      <TablePagination
        compact
        totalRows={totalCount}
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onChangePage={pagination.handlePagination}
        onChangeRowsPerPage={pagination.handleRowsPerPage}
      />
      <Drawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            maxHeight: '90dvh',
            borderRadius: '16px 16px 0 0',
            pb: 'env(safe-area-inset-bottom, 0px)',
            '& input, & .MuiSelect-select': { fontSize: 16 },
            '& button': { minHeight: 44 },
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6">{t('common.filters')}</Typography>
          <IconButton aria-label={t('common.close')} onClick={() => setFiltersOpen(false)}>
            <Close />
          </IconButton>
        </Stack>
        {filters}
        <Stack direction="row" gap={1} p={2}>
          <Button onClick={onClearFilters}>{t('common.mobileList.clearFilters')}</Button>
          <Button variant="contained" onClick={() => setFiltersOpen(false)}>
            {t('common.mobileList.showResults')}
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
};
